import React, { useRef, useState, useCallback, useEffect } from 'react';
import { useThree } from '@react-three/fiber';
import { useThrottledFrame } from '../../hooks/useThrottledFrame';
import { useXR, XROrigin, TeleportTarget } from '@react-three/xr';
import type { Group } from 'three';
import { Vector3, Box3, Mesh } from 'three';
import VRLocomotion from './VRLocomotion';
import VRAvatarBody from './VRAvatarBody';
import VRBuildModeOptimizer from './VRBuildModeOptimizer';
import VRBuildQualityProfile from './VRBuildQualityProfile';
import VRBuildPalette from './build/VRBuildPalette';
import VRHeldItem from './build/VRHeldItem';
import VRGrab from './build/VRGrab';
import VRPlacementTool from './VRPlacementTool';
import { useWorldStore } from '../../stores/worldStore';
import { clampToBounds } from '../../utils/worldBounds';
import VRDebugReadout, { vrDebugEnabled } from './VRDebugReadout';
import VRHandControls from './hands/VRHandControls';
import { usePortalArrival } from '../portals/portals';

const _portalDir = new Vector3();
const _portalHead = new Vector3();
import VRHandManipulator from './build/VRHandManipulator';
import VRLibraryPanel from './build/VRLibraryPanel';

/**
 * VRScene - Provides VR player rig with locomotion + teleportation + collision
 */

// ---- VR Collision: AABB collision against collidable meshes ----
const _vrColBox = new Box3();

const VRCollision: React.FC<{ originRef: React.RefObject<Group | null> }> = ({ originRef }) => {
  const { scene } = useThree();
  // Mesh + world AABB, computed on refresh rather than per frame — an uploaded
  // venue contributes hundreds of collidable meshes.
  const collidableCache = useRef<{ mesh: Mesh; box: Box3 }[]>([]);
  const lastCacheTime = useRef(0);
  const lastPos = useRef({ x: 0, z: 0 });

  useThrottledFrame(() => {
    if (!originRef.current) return;

    const now = performance.now();
    // Refresh collidable cache every 500ms
    if (now - lastCacheTime.current > 500) {
      collidableCache.current = [];
      const NEAR = 30;
      const ox = originRef.current.position.x;
      const oz = originRef.current.position.z;
      scene.traverse((obj) => {
        if (obj.userData?.collidable !== true || !(obj as Mesh).isMesh) return;
        const box = new Box3().setFromObject(obj);
        if (box.isEmpty()) return;
        if (box.max.x < ox - NEAR || box.min.x > ox + NEAR ||
            box.max.z < oz - NEAR || box.min.z > oz + NEAR) return;
        collidableCache.current.push({ mesh: obj as Mesh, box });
      });
      lastCacheTime.current = now;
    }

    // Walkable bounds (settings.bounds): the rig never leaves the room.
    const bounds = useWorldStore.getState().worldBounds;
    if (bounds) {
      const o = originRef.current;
      [o.position.x, o.position.z] = clampToBounds(bounds, o.position.x, o.position.z);
    }

    if (collidableCache.current.length === 0) return;

    const avatarRadius = 0.4;
    const posX = originRef.current.position.x;
    const posZ = originRef.current.position.z;

    // Vertical support: stand on whatever is under the rig. Without this the
    // builder's VR rig keeps its starting height forever, so a venue whose
    // floors are not at y=0 leaves you hanging in the air or sunk inside the
    // building, and stairs do nothing.
    const STEP = 0.6;
    const feet = originRef.current.position.y;
    let support = -Infinity;
    for (const entry of collidableCache.current) {
      const b = entry.box;
      if (posX < b.min.x - 0.05 || posX > b.max.x + 0.05) continue;
      if (posZ < b.min.z - 0.05 || posZ > b.max.z + 0.05) continue;
      if (b.max.y <= feet + STEP && b.max.y > support) support = b.max.y;
    }
    if (support > -Infinity) {
      const dy = support - originRef.current.position.y;
      originRef.current.position.y += dy > 0 ? Math.min(dy, 0.12) : Math.max(dy, -0.25);
    }

    const curY = originRef.current.position.y + 0.5; // torso height above the rig

    for (const entry of collidableCache.current) {
      _vrColBox.copy(entry.box);

      // Anything low enough to step onto (floor tiers, steps, cushions) is
      // footing, never a wall — same rule as the desktop avatar.
      if (_vrColBox.max.y <= originRef.current.position.y + STEP) continue;

      // Skip if no Y overlap
      if (curY < _vrColBox.min.y || curY > _vrColBox.max.y) continue;

      const minX = _vrColBox.min.x - avatarRadius;
      const maxX = _vrColBox.max.x + avatarRadius;
      const minZ = _vrColBox.min.z - avatarRadius;
      const maxZ = _vrColBox.max.z + avatarRadius;

      // Push-out if overlapping
      if (posX > minX && posX < maxX && posZ > minZ && posZ < maxZ) {
        const escapeLeft = posX - minX;
        const escapeRight = maxX - posX;
        const escapeBack = posZ - minZ;
        const escapeFront = maxZ - posZ;
        const minEscape = Math.min(escapeLeft, escapeRight, escapeBack, escapeFront);
        // Deep inside a box means a hollow structure, not solid geometry —
        // pushing out would eject the player through a wall.
        if (minEscape > avatarRadius + 0.6) continue;
        const pushForce = 0.06;

        if (minEscape === escapeLeft) originRef.current.position.x -= pushForce;
        else if (minEscape === escapeRight) originRef.current.position.x += pushForce;
        else if (minEscape === escapeBack) originRef.current.position.z -= pushForce;
        else originRef.current.position.z += pushForce;
      }
    }

    lastPos.current.x = originRef.current.position.x;
    lastPos.current.z = originRef.current.position.z;
  }, 3, true); // essential — collision must run in VR

  return null;
};

// ---- VR Scene ----

interface VRSceneProps {
  avatarUrl?: string;
  worldId?: string;
}

const VRScene: React.FC<VRSceneProps> = ({ avatarUrl, worldId }) => {
  const isPresenting = useXR((s) => !!s.session);
  const originRef = useRef<Group>(null);

  // Put the rig on the world's spawn pad when VR starts. The builder's rig
  // never did this — it began at the origin regardless of where the world's
  // floor actually is, which is why entering VR here could drop you at the
  // base of a building whose decks sit well above y=0.
  const spawnPosition = useWorldStore((s) => s.spawnPosition);
  const placedRef = useRef(false);
  useEffect(() => {
    if (!isPresenting) { placedRef.current = false; return; }
    if (placedRef.current || !originRef.current) return;
    originRef.current.position.set(spawnPosition[0], spawnPosition[1], spawnPosition[2]);
    placedRef.current = true;
  }, [isPresenting, spawnPosition]);

  // The editor's desktop camera (drei <PerspectiveCamera makeDefault>)
  // unmounts on entering VR and hands back a camera that isn't in the rig, so
  // the headset view — and the hands, which follow the camera's parent —
  // stayed at the world origin while the rig moved. Put the rendering camera
  // in the rig for the session, as the viewer's XROrigin does.
  const camera = useThree((s) => s.camera);
  useEffect(() => {
    const o = originRef.current;
    if (!isPresenting || !o || camera.parent === o) return;
    const prev = camera.parent;
    o.add(camera);
    return () => { o.remove(camera); if (prev) prev.add(camera); };
  }, [isPresenting, camera]);

  const handleTeleport = useCallback((point: Vector3) => {
    if (!originRef.current) return;
    originRef.current.position.x = point.x;
    originRef.current.position.z = point.z;
  }, []);
  // Hand teleport lands on the surface the arc found (upper decks, stages).
  const handleHandTeleport = useCallback((point: Vector3) => {
    if (!originRef.current) return;
    originRef.current.position.set(point.x, point.y, point.z);
  }, []);

  // Portal: turn so the headset faces out of the exit, then put it over the arrival point.
  usePortalArrival(({ position, yaw }) => {
    const o = originRef.current;
    if (!isPresenting || !o) return;
    camera.getWorldDirection(_portalDir);
    o.rotation.y += yaw - Math.atan2(_portalDir.x, _portalDir.z);
    o.updateMatrixWorld(true);
    camera.getWorldPosition(_portalHead);
    o.position.x += position[0] - _portalHead.x;
    o.position.z += position[2] - _portalHead.z;
    o.position.y = position[1];
  });

  if (!isPresenting) return null;

  return (
    <>
      <XROrigin ref={originRef}>
        <VRAvatarBody avatarUrl={avatarUrl} />
        <VRHandControls originRef={originRef} throughWalls onTeleport={handleHandTeleport} />
        {vrDebugEnabled() && <VRDebugReadout originRef={originRef} />}
      </XROrigin>

      <TeleportTarget onTeleport={handleTeleport}>
        <mesh name="vr-teleport-floor" rotation={[-Math.PI / 2, 0, 0]} position={[0, -0.49, 0]}>
          <planeGeometry args={[200, 200]} />
          <meshBasicMaterial visible={false} />
        </mesh>
      </TeleportTarget>

      <VRLocomotion
        target={originRef as any}
        moveSpeed={3.0}
        enableTeleport={true}
        enableSmoothLocomotion={true}
        debugThumbstick={false}
      />

      {/* Collision detection — runs after locomotion moves the origin */}
      <VRCollision originRef={originRef} />

      {/* VR build mode optimizer — safe performance tuning */}
      <VRBuildModeOptimizer />

      {/* Quality throttle while actively building — restores on build-mode exit */}
      <VRBuildQualityProfile />

      {/* The wrist palette (Shapes / Edit / Colour / World) — wrist bar or A / X opens it */}
      <VRBuildPalette worldId={worldId || ''} originRef={originRef} />

      {/* Whatever the palette put in your right hand — release to drop it */}
      <VRHeldItem worldId={worldId || ''} />


      {/* VR Placement Tool — ray placement for screens / web panels (everything else is dropped from your hand) */}
      <VRPlacementTool worldId={worldId || ''} />

      {/* Grab anything: pinch (hands) or grip (controllers), near or far */}
      <VRGrab worldId={worldId || ''} />

      {/* Hands: two-hand move / turn / resize of the selection */}
      <VRHandManipulator worldId={worldId || ''} />

      {/* Library & Studio — models to place with the placement ray */}
      <VRLibraryPanel />

    </>
  );
};

export default VRScene;
